import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Button, Input } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader/index.js";
import { UnauthorizedView } from "@/components/UnauthorizedView/index.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { useQuery } from "../../../hooks/useApi.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  platformService,
  type LoadTestAccount,
  type LoadTestPool,
} from "../../../api/platform.service.js";
import { fetchLoadTestPools } from "./availability";
import { LoginCode, type RevealedLoginCode, revealedLoginCode } from "./LoginCode";
import {
  MAX_POOL_SIZE,
  MIN_POOL_SIZE,
  TAG_PREFIX,
  validateLabel,
  validateSize,
} from "./validation";
import styles from "./LoadTestAccounts.module.css";

type ApiError = Error & { status?: number };

function statusOf(error: unknown): number | undefined {
  return (error as ApiError | null)?.status;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function formatTimestamp(value: string | null): string {
  if (!value) return "Never";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

type RevealState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "revealed"; revealed: RevealedLoginCode }
  | { status: "no-login" }
  | { status: "error"; message: string };

function AccountItem({ account, canWrite }: { account: LoadTestAccount; canWrite: boolean }) {
  const [reveal, setReveal] = useState<RevealState>({ status: "idle" });

  async function handleReveal() {
    setReveal({ status: "loading" });
    try {
      const result = await platformService.loadTestAccounts.loginCode(account.email);
      setReveal({
        status: "revealed",
        revealed: revealedLoginCode(result.code, result.expiresAt, result.expiresInSeconds),
      });
    } catch (error) {
      setReveal(
        statusOf(error) === 409
          ? { status: "no-login" }
          : { status: "error", message: messageOf(error) },
      );
    }
  }

  return (
    <li className={styles.account}>
      <span className={styles.email}>{account.email}</span>
      <span className={styles.muted}>Last login: {formatTimestamp(account.lastLoginAt)}</span>
      {canWrite && (
        <Button
          variant="outlined"
          size="small"
          onClick={handleReveal}
          disabled={reveal.status === "loading"}
          aria-label={`Reveal login code for ${account.email}`}
        >
          {reveal.status === "loading" ? "Revealing…" : "Reveal login code"}
        </Button>
      )}
      <span role="status">
        {reveal.status === "revealed" && <LoginCode revealed={reveal.revealed} />}
        {reveal.status === "no-login" && (
          <span>
            No login is in progress for this account. Start the login as this account first, then
            reveal the code.
          </span>
        )}
        {reveal.status === "error" && <span className={styles.error}>{reveal.message}</span>}
      </span>
    </li>
  );
}

type TearDownConfirmationProps = {
  tag: string;
  onCancel: () => void;
  onTornDown: (message: string) => void;
};

function TearDownConfirmation({ tag, onCancel, onTornDown }: TearDownConfirmationProps) {
  const inputId = useId();
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = typed === tag;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!matches) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await platformService.loadTestAccounts.teardown(tag);
      onTornDown(
        `Tore down ${result.tag}: ${result.anonymised} accounts anonymised, SSO cleanup delivered for ${result.ssoCleanupDelivered}, pending for ${result.ssoCleanupPending}.`,
      );
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      className={styles.confirmation}
      onSubmit={handleSubmit}
      aria-label={`Confirm tear-down of ${tag}`}
    >
      <p>
        Tearing down anonymises every account in <code>{tag}</code> straight away, with no grace
        period. Type the full tag to confirm.
      </p>
      <Input
        id={inputId}
        label={`Type ${tag} to confirm`}
        value={typed}
        onChange={event => setTyped(event.target.value)}
        autoComplete="off"
        spellCheck={false}
        autoFocus
        error={error ?? undefined}
      />
      <div className={styles.actions}>
        <Button type="submit" color="error" disabled={!matches || submitting}>
          {submitting ? "Tearing down…" : "Tear down"}
        </Button>
        <Button type="button" variant="text" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

type PoolRowProps = {
  pool: LoadTestPool;
  canWrite: boolean;
  onTornDown: (message: string) => void;
};

function PoolRow({ pool, canWrite, onTornDown }: PoolRowProps) {
  const accountsId = useId();
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const tearDownButton = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);

  useEffect(() => {
    if (confirming || !restoreFocus.current) return;
    restoreFocus.current = false;
    tearDownButton.current?.focus();
  }, [confirming]);

  function handleCancel() {
    restoreFocus.current = true;
    setConfirming(false);
  }

  function handleTornDown(message: string) {
    setConfirming(false);
    onTornDown(message);
  }

  return (
    <>
      <tr>
        <th scope="row">
          <code>{pool.tag}</code>
        </th>
        <td>{pool.size}</td>
        <td>{formatTimestamp(pool.createdAt)}</td>
        <td>{formatTimestamp(pool.lastLoginAt)}</td>
        <td>
          <div className={styles.actions}>
            <Button
              variant="text"
              size="small"
              aria-expanded={expanded}
              aria-controls={accountsId}
              onClick={() => setExpanded(value => !value)}
            >
              {expanded ? "Hide accounts" : "Show accounts"}
            </Button>
            {canWrite && !confirming && (
              <Button
                ref={tearDownButton}
                variant="outlined"
                size="small"
                color="error"
                onClick={() => setConfirming(true)}
                aria-label={`Tear down ${pool.tag}`}
              >
                Tear down
              </Button>
            )}
          </div>
        </td>
      </tr>
      {confirming && (
        <tr>
          <td colSpan={5}>
            <TearDownConfirmation
              tag={pool.tag}
              onCancel={handleCancel}
              onTornDown={handleTornDown}
            />
          </td>
        </tr>
      )}
      {expanded && (
        <tr>
          <td colSpan={5}>
            <ul id={accountsId} className={styles.accounts} aria-label={`Accounts in ${pool.tag}`}>
              {pool.accounts.map(account => (
                <AccountItem key={account.websiteUserId} account={account} canWrite={canWrite} />
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

type CreatePoolFormProps = {
  onCreated: (message: string) => void;
  onFailed: () => void;
};

function CreatePoolForm({ onCreated, onFailed }: CreatePoolFormProps) {
  const labelId = useId();
  const sizeId = useId();
  const [label, setLabel] = useState("");
  const [size, setSize] = useState("10");
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const labelError = attempted ? validateLabel(label) : undefined;
  const sizeError = attempted ? validateSize(size) : undefined;
  const tag = `${TAG_PREFIX}${label}`;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    setServerError(null);
    if (validateLabel(label) || validateSize(size)) return;
    setSubmitting(true);
    try {
      const result = await platformService.loadTestAccounts.create({ tag, size: Number(size) });
      onCreated(`Created ${result.accounts.length} accounts in ${result.tag}.`);
      setLabel("");
      setAttempted(false);
    } catch (error) {
      setServerError(
        statusOf(error) === 409
          ? `The tag ${tag} is already used. Choose another label.`
          : messageOf(error),
      );
      onFailed();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className={styles.createForm} onSubmit={handleSubmit} noValidate aria-label="Create pool">
      <h2 className={styles.heading}>Create a pool</h2>
      <div className={styles.fields}>
        <div className={styles.tagField}>
          <span className={styles.prefix} aria-hidden="true">
            {TAG_PREFIX}
          </span>
          <Input
            id={labelId}
            label="Tag label"
            value={label}
            onChange={event => setLabel(event.target.value.toLowerCase())}
            helperText={`Full tag: ${tag}`}
            error={labelError}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
          />
        </div>
        <Input
          id={sizeId}
          label="Accounts"
          type="number"
          inputMode="numeric"
          min={MIN_POOL_SIZE}
          max={MAX_POOL_SIZE}
          step={1}
          value={size}
          onChange={event => setSize(event.target.value)}
          helperText={`${MIN_POOL_SIZE} to ${MAX_POOL_SIZE}`}
          error={sizeError}
        />
        <Button type="submit" disabled={submitting} className={styles.submit}>
          {submitting ? "Creating…" : "Create pool"}
        </Button>
      </div>
      {serverError && (
        <p role="alert" className={styles.error}>
          {serverError}
        </p>
      )}
    </form>
  );
}

export default function LoadTestAccountsPage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("platform.write");
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeFocusRequest, setNoticeFocusRequest] = useState(0);
  const noticeRef = useRef<HTMLDivElement>(null);
  const { data, loading, error, refetch } = useQuery(() => fetchLoadTestPools(), []);

  useEffect(() => {
    if (noticeFocusRequest > 0) noticeRef.current?.focus();
  }, [noticeFocusRequest]);

  function handleCreated(message: string) {
    setNotice(message);
    void refetch();
  }

  function handleTornDown(message: string) {
    setNotice(message);
    setNoticeFocusRequest(count => count + 1);
    void refetch();
  }

  if (error) {
    const status = statusOf(error);
    if (status === 404) return <Navigate to="/" replace />;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader
          title="Load-test accounts"
          subtitle="Tagged website accounts for load tests and sign-in spot checks on this environment"
        />
      </div>

      <div role="status" ref={noticeRef} tabIndex={-1} className={styles.noticeRegion}>
        {notice && <p className={styles.notice}>{notice}</p>}
      </div>

      {canWrite && <CreatePoolForm onCreated={handleCreated} onFailed={() => void refetch()} />}

      <section className={styles.pools} aria-labelledby="loadtest-pools-heading">
        <h2 id="loadtest-pools-heading" className={styles.heading}>
          Pools
        </h2>
        {loading ? (
          <LoadingState message="Loading pools…" />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Unable to load pools"
            message={error.message}
            action={{ label: "Retry", onClick: () => void refetch() }}
          />
        ) : !data || data.pools.length === 0 ? (
          <EmptyState variant="empty" title="No pools on this environment" />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Tag</th>
                <th scope="col">Accounts</th>
                <th scope="col">Created</th>
                <th scope="col">Last login</th>
                <th scope="col">
                  <span className={styles.visuallyHidden}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.pools.map(pool => (
                <PoolRow
                  key={pool.tag}
                  pool={pool}
                  canWrite={canWrite}
                  onTornDown={handleTornDown}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
